import { useState, useCallback } from "react";
import axios from "../api/axios";

const normalizeOrganization = (q) => q?.organization || q?.orgId;

const normalizeBranch = (branchIds) => {
  if (Array.isArray(branchIds)) {
    return branchIds.find(Boolean);
  }
  return branchIds || undefined;
};

export default function useItemCoverage() {
  const [loading, setLoading] = useState(false);
  const [mapping, setMapping] = useState({ items: [], collections: [] });
  const [data, setData] = useState(null);
  const xlMime =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  const fetchStockMapping = useCallback(async (q) => {
    // q: { organization|orgId, academicYear, branchIds, grade, program? }
    setLoading(true);
    try {
      const organization = normalizeOrganization(q);
      const academicYear = q?.academicYear;
      const branch = normalizeBranch(q?.branchIds);
      const grade = q?.grade;

      if (!organization || !academicYear || !branch || !grade) {
        setMapping({ items: [], collections: [] });
        return;
      }

      const res = await axios.get("/sale-item-mapping/", {
        params: {
          organization,
          academicYear,
          branch,
          grade,
        },
      });
      const mappings = res.data || [];
      const uniqueItemsMap = new Map();
      const uniqueCollectionsMap = new Map();

      mappings.forEach((mapping) => {
        (mapping.items || []).forEach((itemObj) => {
          const id = itemObj.item?._id;
          if (id && !uniqueItemsMap.has(id)) {
            uniqueItemsMap.set(id, itemObj);
          }
        });
        (mapping.collections || []).forEach((colObj) => {
          const id = colObj.collection?._id;
          if (id && !uniqueCollectionsMap.has(id)) {
            uniqueCollectionsMap.set(id, colObj);
          }
        });
      });

      setMapping({
        items: Array.from(uniqueItemsMap.values()),
        collections: Array.from(uniqueCollectionsMap.values()),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchItemCoverage = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await axios.get("/items/report/item-coverage", { params: q });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  const exportItemCoverage = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await axios.get("/items/report/item-coverage/export", {
        params: q,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: xlMime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 16); // YYYY-MM-DD
      a.href = url;
      a.download = `item_coverage_${q.grade || "all-grades"}_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    mapping,
    data,
    setData,
    fetchStockMapping,
    fetchItemCoverage,
    exportItemCoverage,
  };
}
